<template>
  <div class="rounded-lg border border-gray-200 overflow-hidden bg-white">
    <div class="flex justify-between items-center p-6">
      <h2 class="text-lg font-semibold text-gray-900">Products</h2>
      <Button v-if="props.canEdit" @click="onShowProductModal()" variant="outline">
        <Plus class="h-4 w-4" />
        Add Product
      </Button>
    </div>
    <div class="border-t border-gray-200">
      <table v-if="order?.products" class="w-full table-auto text-sm text-left">
        <thead class="bg-grey-light-100 text-grey-light-600 font-medium border-b border-gray-200">
          <tr>
            <th class="px-4 py-2">Description</th>
            <th class="px-4 py-2">SKU</th>
            <th class="px-4 py-2 text-right">Amount</th>
            <th class="px-4 py-2">Product Link</th>
            <th v-if="props.canEdit && order.products.length > 1" class="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody class="text-grey-light-600">
          <tr v-for="product in order.products" :key="product._id" class="border-b border-gray-200">
            <td
              @click="canEdit ? onShowProductModal(product) : ''"
              class="p-5 flex items-center gap-2 hover:cursor-pointer"
            >
              {{ product.name ? product.name : order.name }}
              <SquarePen v-if="canEdit" class="w-4 h-4" />
            </td>
            <td class="p-5">
              {{ product.sku }}
            </td>
            <td class="p-5 text-right">
              {{ (product.sku && product.sku.includes('cancelled')) || (product.amount || 0) < 0 ? '-' : '' }}
              {{ displayCurrency(product.amount || 0) }}
            </td>
            <td class="p-5">
              {{ product.link ? product.link : 'N/A' }}
            </td>
            <td v-if="props.canEdit && order.products.length > 1" class="p-5">
              <button @click="onConfirmDelete(product)" class="text-red-500 hover:text-red-700">
                <Trash2 class="w-4 h-4" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="p-6 text-gray-500">
        <p>No products available.</p>
      </div>
    </div>
    <ProductModal
      :order-id="order?._id!"
      :show="showProductModal"
      :selected-product="selectedProduct"
      @update:show="showProductModal = false"
    />

    <Dialog :open="showDeleteConfirm" @update:open="showDeleteConfirm = false">
      <DialogContent class="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Remove Product</DialogTitle>
        </DialogHeader>
        <p class="text-sm text-gray-600 text-center mb-3">Are you sure you want to remove this product?</p>
        <div class="rounded-md border border-gray-200 divide-y divide-gray-200 text-sm">
          <div class="flex justify-between px-4 py-2">
            <span class="text-gray-500">Description</span>
            <span class="font-medium text-gray-900">{{ productToDelete?.name }}</span>
          </div>
          <div class="flex justify-between px-4 py-2">
            <span class="text-gray-500">SKU</span>
            <span class="font-medium text-gray-900">{{ productToDelete?.sku }}</span>
          </div>
          <div class="flex justify-between px-4 py-2">
            <span class="text-gray-500">Amount</span>
            <span class="font-medium text-gray-900">{{ displayCurrency(productToDelete?.amount) }}</span>
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <Button variant="outline" @click="showDeleteConfirm = false">Cancel</Button>
          <Button variant="destructive" @click="onDelete" :disabled="isDeleting">
            <LayupSpinner v-if="isDeleting" :size="4" />
            <span>Remove</span>
          </Button>
        </div>
        <p v-if="deleteError" class="text-sm font-medium text-destructive text-center mt-2">{{ deleteError }}</p>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NetworkError } from '@/errors';
import { getOrderByIdWithPlansPaymentsAndConnectors } from '@/services/orders';
import { useOrderStore } from '@/stores/orders';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { DeleteOrderProduct } from '@/utils/order.utils';
import { Plus, SquarePen, Trash2 } from 'lucide-vue-next';
import { ref } from 'vue';
import type { ModelOrderResponse, ModelProductResponse } from '@layup/api-sdk';
import ProductModal from '../ProductModal.vue';

const props = defineProps<{
  canEdit: boolean;
  order: ModelOrderResponse | null | undefined;
}>();

const orderStore = useOrderStore();

const showProductModal = ref(false);
const selectedProduct = ref<ModelProductResponse>();

const showDeleteConfirm = ref(false);
const productToDelete = ref<ModelProductResponse>();
const isDeleting = ref(false);
const deleteError = ref('');

const onShowProductModal = (product?: ModelProductResponse) => {
  selectedProduct.value = product || undefined;
  showProductModal.value = true;
};

const onConfirmDelete = (product: ModelProductResponse) => {
  productToDelete.value = product;
  deleteError.value = '';
  showDeleteConfirm.value = true;
};

const onDelete = async () => {
  if (!productToDelete.value || !props.order?._id) return;
  try {
    isDeleting.value = true;
    deleteError.value = '';
    await DeleteOrderProduct(props.order._id, productToDelete.value as any);
    const updatedOrder = await getOrderByIdWithPlansPaymentsAndConnectors(props.order._id);
    orderStore.updateOrder(updatedOrder);
    showDeleteConfirm.value = false;
    addMetric('product-delete', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('product-delete', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('product-delete', { status: 'error', channel: 'merchant' });
    }
    deleteError.value = 'Something went wrong, failed to remove product.';
  } finally {
    isDeleting.value = false;
  }
};
</script>
